import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { apiClient } from "../../../api/client";
import { useAuth } from "../../../app/providers/AuthProvider";
import { useStudentBalance } from "../../student/hooks/useStudentBalance";
import { useStudentCycles } from "../../student/hooks/useStudentCycles";
import { StatCard, QuickAction, SectionTitle, DashboardGreeting } from "../components/DashboardKit";
import { formatTime12h } from "../../../utils/formatTime";

function useMyClasses() {
  return useQuery({
    queryKey: ["student", "my-classes"],
    queryFn: () => apiClient.get("/api/v1/attendances/").then((r) => r.data),
  });
}

function StudentDashboard() {
  const { slug } = useParams();
  const { user } = useAuth();
  const name = user?.name || user?.email || "Estudiante";
  const base = `/${slug}/app`;

  const { data: balance } = useStudentBalance();
  const { data: cycles } = useStudentCycles();
  const { data: classes } = useMyClasses();

  const upcoming = useMemo(
    () =>
      (classes ?? [])
        .filter((c) => c.status === "UPCOMING")
        .sort((a, b) => dayjs(a.date) - dayjs(b.date)),
    [classes]
  );
  const nextClass = upcoming[0] ?? null;
  const hoursBalance = Number(balance?.hours_balance ?? 0);
  const activePackages = (balance?.active_subscriptions ?? []).length;

  return (
    <div className="container-fluid px-0 py-3">
      <DashboardGreeting
        name={name}
        subtitle="Tu espacio de aprendizaje — saldo, clases y progreso."
      />

      <div className="row g-3">
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-wallet-outline"
            variant="success"
            label="Saldo de horas"
            value={`${hoursBalance} h`}
            sub={`${activePackages} paquete(s) activo(s)`}
            to={`${base}/mi-espacio/mi-saldo`}
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-calendar-check"
            variant="primary"
            label="Clases agendadas"
            value={upcoming.length}
            to={`${base}/mi-espacio/mis-clases`}
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-book-open-page-variant"
            variant="info"
            label="Ciclos de estudio"
            value={cycles?.total_cycles ?? 0}
            sub={`${cycles?.total_months ?? 0} mes(es)`}
            to={`${base}/perfil`}
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-calendar-plus"
            variant="warning"
            label="Agendar clase"
            value="Ir"
            to={`${base}/mi-espacio/calendario`}
          />
        </div>
      </div>

      <SectionTitle>Tu próxima clase</SectionTitle>
      <Card className="border-0 shadow-sm">
        <Card.Body>
          {nextClass ? (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <div className="fw-semibold">{nextClass.topic ?? "Clase"}</div>
                <div className="text-muted small">
                  {dayjs(nextClass.date).format("dddd D [de] MMMM")}
                  {nextClass.time ? ` · ${formatTime12h(nextClass.time)}` : ""}
                  {nextClass.location ? ` · ${nextClass.location}` : ""}
                </div>
              </div>
              <Link
                to={`${base}/mi-espacio/mis-clases`}
                className="btn btn-outline-primary"
                style={{ minHeight: 44 }}
              >
                Ver mis clases →
              </Link>
            </div>
          ) : (
            <p className="text-muted mb-0">
              No tienes clases agendadas.{" "}
              <Link to={`${base}/mi-espacio/calendario`}>
                Agenda una desde el calendario
              </Link>
              .
            </p>
          )}
        </Card.Body>
      </Card>

      <SectionTitle>Accesos rápidos</SectionTitle>
      <div className="row g-2">
        <div className="col-6 col-md-3">
          <QuickAction to={`${base}/mi-espacio/calendario`} icon="mdi-calendar-plus" label="Agendar clase" variant="primary" />
        </div>
        <div className="col-6 col-md-3">
          <QuickAction to={`${base}/mi-espacio/mis-clases`} icon="mdi-book-open-variant" label="Mis clases" />
        </div>
        <div className="col-6 col-md-3">
          <QuickAction to={`${base}/mi-espacio/mi-saldo`} icon="mdi-wallet-outline" label="Mi saldo" />
        </div>
        <div className="col-6 col-md-3">
          <QuickAction to={`${base}/mi-espacio/mis-pagos`} icon="mdi-receipt" label="Mis pagos" />
        </div>
      </div>
    </div>
  );
}

export { StudentDashboard };
